import axios from "axios";

// Resolve the backend host the frontend should call.
// - In production on a custom domain (e.g. world-othello.com) the build-time
//   REACT_APP_BACKEND_URL points to the original Emergent host, which makes every
//   API call cross-origin. Browsers (Safari ITP, Firefox ETP, Brave) then treat
//   the requests as third-party and may silently throttle or block them, which
//   manifests as the page hanging.
// - Since the platform already proxies /api/* on every custom domain to the
//   same backend, we prefer the current page's origin whenever it differs from
//   the build-time value. This keeps requests same-origin and avoids preflights.
function resolveBackendOrigin() {
  const envValue = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return envValue;
  const pageOrigin = window.location.origin;
  if (!envValue) return pageOrigin;
  try {
    const envHost = new URL(envValue).host;
    const pageHost = new URL(pageOrigin).host;
    // Same host (or running under env URL) — keep the env value.
    if (envHost === pageHost) return envValue;
  } catch {
    return envValue;
  }
  // Different host — running on a custom domain. Use same-origin to stay
  // first-party from the browser's point of view.
  return pageOrigin;
}

const BACKEND_URL = resolveBackendOrigin();
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Auto-redirect to login on session expiry (401 Unauthorized).
// We skip /auth/* endpoints because:
//   - /auth/me intentionally allows 401 for anonymous visitors
//   - /auth/login & /auth/register surface 401s as form errors
//   - /auth/logout is best-effort and may 401 after the cookie expires
// For everything else, a 401 means the JWT cookie expired mid-session, so we
// notify the user and bounce them to /login (preserving their intended path).
let redirectingForAuth = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isAuthEndpoint = url.includes("/auth/");
    if (
      status === 401 &&
      !isAuthEndpoint &&
      typeof window !== "undefined" &&
      !redirectingForAuth
    ) {
      redirectingForAuth = true;
      const fromPath = window.location.pathname + window.location.search;
      try {
        // window.location.replace triggers a full reload, so any in-memory
        // toast queue is lost. Persist a flag the LoginPage reads on mount.
        sessionStorage.setItem("auth_session_expired", "1");
      } catch {
        // sessionStorage can be unavailable (private mode, quota) — ignore.
      }
      const next = encodeURIComponent(fromPath);
      window.location.replace(`/login?next=${next}`);
    }
    return Promise.reject(error);
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function imageUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${API_BASE}/files/${path}`;
}
